import { createFileRoute } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Globe } from "lucide-react";
import { ContactForm } from "#/components/pages/contact";
import { PageHeader } from "#/components/shared/PageHeader";
import { BUSINESS } from "#/lib/constants";
import { HEADER } from "#/lib/images";

const TITLE = "Contact Blue Rock Concrete";
const DESCRIPTION =
  "Reach Blue Rock Concrete for general questions, callback requests, or commercial inquiries. Call, email, or send a message and we'll respond the same business day.";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://bluerockconcreteco.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://bluerockconcreteco.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <main>
      <PageHeader
        eyebrow="Contact"
        title="Get in touch"
        intro="Questions, callback requests, or a commercial inquiry. Reach us directly, or send a message below and we'll get back to you the same business day."
        image={HEADER.contact.src}
        imageMobile={HEADER.contact.mobile}
        imageAlt={HEADER.contact.alt}
      />

      {/* Info-led layout: a dark display-only info column beside a light,
          simple stacked form. Deliberately distinct from /quote's bordered
          spec-sheet estimate form (see memory/lessons.md 2026-07-25). */}
      <section className="bg-light-bg py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-5 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="space-y-4">
                <InfoCard icon={Phone} label="Phone" value={BUSINESS.phone} href={BUSINESS.phoneHref} />
                <InfoCard
                  icon={Mail}
                  label="Email"
                  value={BUSINESS.email}
                  href={`mailto:${BUSINESS.email}`}
                />
                <InfoCard
                  icon={MapPin}
                  label="Service Area"
                  value={BUSINESS.serviceAreas.join(", ")}
                />
                <InfoCard icon={Clock} label="Hours" value={BUSINESS.hours} />
              </div>

              <div className="mt-6 flex items-center gap-3">
                <SocialLink href={BUSINESS.social.facebook} label="Facebook" icon={Facebook} />
                <SocialLink href={BUSINESS.social.instagram} label="Instagram" icon={Instagram} />
                <SocialLink href={BUSINESS.social.google} label="Find us on Google" icon={Globe} />
              </div>
            </div>

            <div className="mt-10 lg:col-span-3 lg:mt-0">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function InfoCard({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
}) {
  const className =
    "flex items-start gap-4 border border-white/10 bg-graphite p-6 transition-colors" +
    (href ? " hover:border-flame/40" : "");
  const inner = (
    <>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-flame text-white">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-[0.7rem] font-bold uppercase tracking-[0.15em] text-concrete-light">
          {label}
        </p>
        <p className="mt-1 text-base font-semibold break-words text-white">{value}</p>
      </div>
    </>
  );

  if (href) {
    return (
      <a href={href} className={`${className} no-underline`}>
        {inner}
      </a>
    );
  }
  return <div className={className}>{inner}</div>;
}

function SocialLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center border border-white/10 bg-graphite text-concrete-light transition-colors hover:bg-flame hover:text-white"
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}
